import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'yabatho_cart';

const normalizeId = (value) => String(value ?? '');

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);

      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(Array.isArray(parsedCart) ? parsedCart : []);
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
      setCartItems([]);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item) => {
    if (!item?.id) return;

    setCartItems((prevItems) => {
      const existingItem = prevItems.find(
        (cartItem) => normalizeId(cartItem.id) === normalizeId(item.id),
      );

      if (existingItem) {
        return prevItems.map((cartItem) =>
          normalizeId(cartItem.id) === normalizeId(item.id)
            ? { ...cartItem, quantity: Number(cartItem.quantity || 0) + 1 }
            : cartItem,
        );
      }

      return [
        ...prevItems,
        {
          ...item,
          quantity: 1,
        },
      ];
    });
  };

  const removeFromCart = (id, removeAll = false) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find(
        (cartItem) => normalizeId(cartItem.id) === normalizeId(id),
      );

      if (!existingItem) return prevItems;

      if (removeAll || Number(existingItem.quantity || 0) <= 1) {
        return prevItems.filter(
          (cartItem) => normalizeId(cartItem.id) !== normalizeId(id),
        );
      }

      return prevItems.map((cartItem) =>
        normalizeId(cartItem.id) === normalizeId(id)
          ? { ...cartItem, quantity: Number(cartItem.quantity || 0) - 1 }
          : cartItem,
      );
    });
  };

  const updateQuantity = (id, quantity) => {
    const numericQuantity = Number(quantity);

    if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) {
      removeFromCart(id, true);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((cartItem) =>
        normalizeId(cartItem.id) === normalizeId(id)
          ? { ...cartItem, quantity: numericQuantity }
          : cartItem,
      ),
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const cartCount = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }, [cartItems]);

  const value = {
    cartItems,
    cartCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used inside CartProvider');
  }

  return context;
};
